import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import { ApiCall, GetApiCall } from '../../helper/axios';
import CommonPopup from '../common-popup/CommonPopup';
import imgBefore from '../../assets/img/passport_before.png';
import imgAfter from '../../assets/img/passport_after.png';
import imgBefore2 from '../../assets/img/passport_before2.png';
import imgAfter2 from '../../assets/img/passport_after2.png';
import { getUserToken, formatePriceFn, formatPrice } from '../../helper/commonFunction';
import { getActiveCode, getActiveRate } from '../../helper/currencyState';

/* ─── Inline SVG atoms ─────────────────────────────────────────────────────── */
const Ico = ({ d, size = 16, viewBox = '0 0 24 24', fill = 'none', sw = 2, ...rest }) => (
    <svg viewBox={viewBox} width={size} height={size} fill={fill}
        stroke="currentColor" strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" {...rest}>
        {typeof d === 'string' ? <path d={d} /> : d}
    </svg>
);

const IcoCheck = ({ size = 12 }) => <Ico size={size} sw={2.5} d={<polyline points="20 6 9 17 4 12" />} />;
const IcoCross = ({ size = 12 }) => <Ico size={size} sw={2.5} d={<><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></>} />;
const IcoWarn = ({ size = 15 }) => <Ico size={size} sw={2} d={<><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></>} />;
const IcoArrow = ({ size = 16 }) => <Ico size={size} d={<><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></>} />;
const IcoBack = ({ size = 14 }) => <Ico size={size} d={<polyline points="15 18 9 12 15 6" />} />;
const IcoDL = ({ size = 14 }) => <Ico size={size} d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></>} />;
const IcoRestart = ({ size = 14 }) => <Ico size={size} d={<><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.47" /></>} />;
const IcoUpload = ({ size = 44 }) => <Ico size={size} sw={1.3} d={<><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></>} style={{ color: 'var(--hw-gray-400)' }} />;
const IcoInfo = ({ size = 14 }) => <Ico size={size} d={<><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></>} />;
const IcoCamera = ({ size = 20 }) => <Ico size={size} d={<><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></>} style={{ color: '#fff' }} />;
const IcoClock = ({ size = 18 }) => <Ico size={size} d={<><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></>} />;
const IcoTrash = ({ size = 13 }) => <Ico size={size} d={<><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M10 11v6" /><path d="M14 11v6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></>} />;
const IcoChevL = ({ size = 16 }) => <Ico size={size} d={<polyline points="15 18 9 12 15 6" />} />;
const IcoChevR = ({ size = 16 }) => <Ico size={size} d={<polyline points="9 18 15 12 9 6" />} />;
const IcoCalendar = ({ size = 12 }) => <Ico size={size} d={<><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>} />;
const IcoHash = ({ size = 12 }) => <Ico size={size} d={<><line x1="4" y1="9" x2="20" y2="9" /><line x1="4" y1="15" x2="20" y2="15" /><line x1="10" y1="3" x2="8" y2="21" /><line x1="16" y1="3" x2="14" y2="21" /></>} />;

/* ─── Score ring ───────────────────────────────────────────────────────────── */
function ScoreRing({ score }) {
    const r = 40, circ = 2 * Math.PI * r;
    const colour = score >= 80 ? 'var(--hw-green)' : score >= 60 ? '#d97706' : 'var(--hw-red)';
    return (
        <svg viewBox="0 0 100 100" width="90" height="90">
            <circle cx="50" cy="50" r={r} fill="none" stroke="var(--hw-gray-200)" strokeWidth="9" />
            <circle cx="50" cy="50" r={r} fill="none" stroke={colour} strokeWidth="9"
                strokeDasharray={circ.toFixed(1)}
                strokeDashoffset={(circ * (1 - score / 100)).toFixed(1)}
                strokeLinecap="round" transform="rotate(-90 50 50)"
                style={{ transition: 'stroke-dashoffset .8s ease' }} />
            <text x="50" y="46" textAnchor="middle" dominantBaseline="middle"
                style={{ fontSize: '22px', fontWeight: 800, fill: colour }}>{score}</text>
            <text x="50" y="64" textAnchor="middle"
                style={{ fontSize: '7px', fill: '#8B8B8B', fontWeight: 600, letterSpacing: '0.05em' }}>SCORE</text>
        </svg>
    );
}

/* ─── Stepper ──────────────────────────────────────────────────────────────── */
function Stepper({ step }) {
    const steps = ['Choose country', 'Upload photo', 'Get your photo'];
    const pct = ((step - 1) / (steps.length - 1)) * 100;
    return (
        <div className="hw-pe--stepper-wrap mb-3">
            <div className="hw-pe--stepper-track">
                <div className="hw-pe--stepper-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="hw-pe--stepper-steps">
                {steps.map((label, i) => {
                    const n = i + 1, done = n < step, active = n === step;
                    return (
                        <div key={n} className={`hw-pe--step-item${active ? ' active' : done ? ' done' : ''}`}>
                            <div className="hw-pe--step-circle">
                                {done ? <IcoCheck size={13} /> : n}
                            </div>
                            <span className="hw-pe--step-label hw-text12 hw-fontBold">{label}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ─── react-select styles (match project design system) ────────────────────── */
const rsStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '5rem',
        borderRadius: '0.8rem',
        border: state.isFocused
            ? '1.5px solid var(--hw-brand-orange)'
            : '1.5px solid var(--hw-gray-200)',
        boxShadow: state.isFocused
            ? '0 0 0 3px rgba(var(--hw-brand-orange-rgb), 0.12)'
            : 'none',
        cursor: 'pointer',
        transition: 'border-color .2s, box-shadow .2s',
        padding: '0 0.4rem',
        '&:hover': { borderColor: 'var(--hw-brand-orange)' },
    }),
    valueContainer: (base) => ({ ...base, gap: '0.4rem', padding: '0.6rem 0.8rem' }),
    singleValue: (base) => ({ ...base, fontSize: '1.4rem', fontWeight: 700, color: '#121826' }),
    placeholder: (base) => ({ ...base, fontSize: '1.4rem', color: 'var(--hw-gray-500)' }),
    input: (base) => ({ ...base, fontSize: '1.4rem' }),
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: (base, state) => ({
        ...base,
        color: 'var(--hw-gray-500)',
        transition: 'transform .2s',
        transform: state.selectProps.menuIsOpen ? 'rotate(180deg)' : 'none',
    }),
    menu: (base) => ({
        ...base,
        borderRadius: '0.8rem',
        border: '1.5px solid var(--hw-gray-200)',
        boxShadow: '0 8px 28px rgba(0,0,0,.12)',
        zIndex: 9999,
        overflow: 'hidden',
    }),
    menuList: (base) => ({ ...base, padding: '0.4rem', maxHeight: '28rem' }),
    option: (base, state) => ({
        ...base,
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        padding: '0.9rem 1rem',
        borderRadius: '0.6rem',
        backgroundColor: state.isSelected
            ? 'var(--hw-brand-orange-light)'
            : state.isFocused
                ? 'var(--hw-gray-300)'
                : 'transparent',
        color: state.isSelected ? 'var(--hw-brand-orange)' : '#121826',
        cursor: 'pointer',
        fontSize: '1.4rem',
        fontWeight: state.isSelected ? 700 : 400,
    }),
    noOptionsMessage: (base) => ({ ...base, fontSize: '1.3rem', color: 'var(--hw-gray-500)' }),
};

/* react-select custom label renderer */
const formatOptionLabel = (option, { context }) => {
    if (context === 'value') {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                <span style={{ fontSize: '2rem', lineHeight: 1 }}>{option.flag}</span>
                <div>
                    <div style={{ fontSize: '1.4rem', fontWeight: 700, color: '#121826', lineHeight: 1.3 }}>
                        {option.name}
                    </div>
                    <div style={{ fontSize: '1.1rem', color: 'var(--hw-gray-500)', lineHeight: 1.3 }}>
                        {option.widthMm}×{option.heightMm} mm &nbsp;·&nbsp; {option.backgroundDesc || 'White background'}
                    </div>
                </div>
            </div>
        );
    }
    // menu item
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.9rem', width: '100%' }}>
            <span style={{ fontSize: '1.8rem', lineHeight: 1, flexShrink: 0 }}>{option.flag}</span>
            <div style={{ flex: 1 }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 600, color: '#121826' }}>{option.name}</div>
                <div style={{ fontSize: '1.15rem', color: 'var(--hw-gray-500)' }}>
                    {option.widthMm}×{option.heightMm} mm &nbsp;·&nbsp; {option.backgroundDesc || 'White background'}
                </div>
            </div>
        </div>
    );
};

/* ─── Spec preview card (Step 1 right column) ──────────────────────────────── */
function SpecPreview({ country }) {
    if (!country) return null;
    return (
        <div className="hw-pe--spec-preview">
            <div className="hw-pe--sp-title mb-2">Photo requirements</div>
            <div className="d-flex align-items-center hw-gapy8 mb-2">
                <span style={{ fontSize: '1.8rem', lineHeight: 1 }}>{country.flag}</span>
                <span className="hw-text16 hw-fontBold hw-black900">{country.name}</span>
            </div>
            <span className="hw-pe--sp-dims hw-text12 hw-fontBold mb-2 d-inline-block">
                {country.widthMm}×{country.heightMm} mm &nbsp;·&nbsp; {country.widthPx}×{country.heightPx} px &nbsp;·&nbsp; {country.dpi || 300} DPI
            </span>
            <ul className="hw-pe--sp-reqs mt-1">
                {(country.requirements || []).map((r, i) => (
                    <li key={i} className="d-flex align-items-start hw-gapx8 hw-text12 hw-black500 mb-1">
                        <span className="hw-pe--req-icon"><IcoCheck size={8} /></span>
                        <span>{r}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/* ─── Drop zone ────────────────────────────────────────────────────────────── */
function DropZone({ onFile }) {
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef(null);
    return (
        <div
            className={`hw-pe--drop-zone${dragging ? ' dragover' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files[0]) onFile(e.dataTransfer.files[0]); }}
        >
            <IcoUpload />
            <p className="hw-text14 hw-fontBold hw-black500 mb-02 mt-1">
                Drop your photo here, or{' '}
                <strong style={{ color: 'var(--hw-brand-orange)' }}>click to browse</strong>
            </p>
            <p className="hw-text12 hw-grayk500">JPEG · PNG · WEBP · TIFF · max 15 MB</p>
            <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/tiff"
                style={{ display: 'none' }} onChange={e => e.target.files[0] && onFile(e.target.files[0])} />
        </div>
    );
}

/* ─── Camera capture ───────────────────────────────────────────────────────── */
function CameraCapture({ onCapture }) {
    const videoRef  = useRef(null);
    const streamRef = useRef(null);
    const [facingMode, setFacingMode] = useState('user');
    const [error, setError] = useState('');
    const [ready, setReady] = useState(false);

    const stopStream = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        }
    }, []);

    const startStream = useCallback(async (mode) => {
        setError('');
        setReady(false);
        stopStream();
        if (!navigator.mediaDevices?.getUserMedia) {
            setError('Camera is not supported on this device or browser.');
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: mode, width: { ideal: 1280 }, height: { ideal: 1280 } },
                audio: false,
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play().catch(() => {});
            }
            setReady(true);
        } catch (err) {
            if (err?.name === 'NotAllowedError' || err?.name === 'SecurityError') {
                setError('Camera permission denied. Please allow camera access and try again.');
            } else if (err?.name === 'NotFoundError' || err?.name === 'OverconstrainedError') {
                setError('No camera was found on this device.');
            } else {
                setError('Could not start the camera. Please try again.');
            }
        }
    }, [stopStream]);

    useEffect(() => {
        startStream(facingMode);
        return stopStream;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facingMode]);

    const capture = () => {
        const video = videoRef.current;
        if (!video || !video.videoWidth) return;
        const canvas = document.createElement('canvas');
        canvas.width  = video.videoWidth;
        canvas.height = video.videoHeight;
        // Draw the true (un-mirrored) frame so the saved photo matches reality,
        // even though the live preview is mirrored for a natural selfie feel.
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((blob) => {
            if (!blob) { setError('Capture failed, please try again.'); return; }
            const file = new File([blob], `capture-${Date.now()}.jpg`, { type: 'image/jpeg' });
            stopStream();
            onCapture(file);
        }, 'image/jpeg', 0.95);
    };

    return (
        <div className="hw-pe--drop-zone" style={{ cursor: 'default', padding: '0.75rem' }}>
            {error ? (
                <div style={{ textAlign: 'center' }}>
                    <p className="hw-text14 hw-fontBold hw-black500 mb-1">{error}</p>
                    <button type="button" className="hw-btn hw-btn--outline" onClick={() => startStream(facingMode)}>
                        Retry
                    </button>
                </div>
            ) : (
                <>
                    <div style={{
                        position: 'relative', width: '100%', maxWidth: 360, margin: '0 auto',
                        aspectRatio: '1 / 1', background: '#000', borderRadius: 12, overflow: 'hidden',
                    }}>
                        <video ref={videoRef} playsInline muted
                            style={{
                                width: '100%', height: '100%', objectFit: 'cover',
                                transform: facingMode === 'user' ? 'scaleX(-1)' : 'none',
                            }} />
                    </div>
                    <div className="d-flex align-items-center justify-content-center mt-2" style={{ gap: 10 }}>
                        <button type="button" className="hw-btn hw-btn--primary" disabled={!ready}
                            onClick={capture} style={{ opacity: ready ? 1 : 0.5 }}>
                            Capture
                        </button>
                        <button type="button" className="hw-btn hw-btn--outline"
                            onClick={() => setFacingMode(m => (m === 'user' ? 'environment' : 'user'))}>
                            Switch camera
                        </button>
                    </div>
                    <p className="hw-text12 hw-grayk500 mt-1" style={{ textAlign: 'center' }}>
                        Position your face inside the frame, then tap Capture.
                    </p>
                </>
            )}
        </div>
    );
}

/* ─── Progress panel ───────────────────────────────────────────────────────── */
function ProgressPanel({ steps, idx, pct }) {
    return (
        <div className="hw-pe--progress-wrap mt-2 mb-2">
            <div className="d-flex-column hw-gapx4 mb-2">
                {steps.map((label, i) => (
                    <div key={i} className={`hw-pe--ps-item d-flex align-items-center hw-gapy8 hw-text14${i === idx ? ' active' : i < idx ? ' done' : ''}`}>
                        <span className="hw-pe--ps-dot" />
                        {label}
                    </div>
                ))}
            </div>
            <div className="hw-pe--progress-bar-track">
                <div className="hw-pe--progress-bar-fill" style={{ width: `${pct}%` }} />
            </div>
        </div>
    );
}

/* ─── AI Analysis panel ────────────────────────────────────────────────────── */
function AnalysisPanel({ analysis }) {
    if (!analysis) return null;
    const score = Math.round(analysis.overallScore || 0);

    const chips = [
        { label: 'Face detected', ok: analysis.faceDetected },
        { label: 'Facing forward', ok: analysis.facingForward },
        { label: 'Eyes open', ok: analysis.eyesOpen },
        { label: 'Neutral expression', ok: analysis.neutralExpression },
        { label: 'No glasses', ok: !analysis.glassesDetected },
        { label: 'Background OK', ok: analysis.backgroundSuitable },
        {
            label: `Lighting: ${analysis.lightingQuality || '?'}`,
            ok: analysis.lightingQuality === 'good',
            warn: analysis.lightingQuality === 'fair',
        },
    ];

    return (
        <div className="hw-search--flightFilterResult__listItem mb-3">
            <div className="d-flex align-items-center hw-gapy10 pb-2 mb-2" style={{ borderBottom: '1px solid var(--hw-gray-200)' }}>
                <div style={{ flex: 1 }}>
                    <div className="hw-text16 hw-fontBold hw-black900">Photo Quality Analysis</div>
                    <div className="hw-text12 hw-grayk500">Automated quality check results</div>
                </div>
            </div>
            <div className="hw-pe--analysis-grid">
                <div style={{ textAlign: 'center' }}>
                    <ScoreRing score={score} />
                    <div className="hw-text12 hw-fontWeight--500 hw-grayk500 mt-1">
                        {score >= 80 ? 'Great!' : score >= 60 ? 'Acceptable' : 'Needs work'}
                    </div>
                </div>
                <div>
                    <div className="hw-text12 hw-fontBold hw-text--uppercase hw-red mb-1">Issues</div>
                    <ul className="hw-pe--al-list">
                        {(analysis.issues || []).length > 0
                            ? analysis.issues.map((it, i) => (
                                <li key={i} className="d-flex align-items-start hw-gapy8 hw-text12 hw-black500">
                                    <span className="hw-pe--dot" style={{ background: 'var(--hw-red)' }} />{it}
                                </li>
                            ))
                            : <li className="hw-text12 hw-grayk500" style={{ fontStyle: 'italic' }}>None found</li>}
                    </ul>
                </div>
                <div>
                    <div className="hw-text12 hw-fontBold hw-text--uppercase hw-success mb-1">Looks good</div>
                    <ul className="hw-pe--al-list">
                        {(analysis.positives || []).length > 0
                            ? analysis.positives.map((it, i) => (
                                <li key={i} className="d-flex align-items-start hw-gapy8 hw-text12 hw-black500">
                                    <span className="hw-pe--dot" style={{ background: 'var(--hw-green)' }} />{it}
                                </li>
                            ))
                            : <li className="hw-text12 hw-grayk500" style={{ fontStyle: 'italic' }}>—</li>}
                    </ul>
                </div>
                <div className="d-flex flex-wrap hw-gapy8 hw-gapx8 mt-2"
                    style={{ gridColumn: '1 / -1', paddingTop: '1rem', borderTop: '1px solid var(--hw-gray-200)' }}>
                    {chips.map((c, i) => {
                        const cls = c.warn ? 'hw-pe--chip-warn' : c.ok ? 'hw-pe--chip-ok' : 'hw-pe--chip-bad';
                        return (
                            <span key={i} className={`hw-pe--chip hw-text12 hw-fontBold ${cls}`}>
                                {c.ok ? <IcoCheck /> : <IcoCross />}
                                {c.label}
                            </span>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

/* ─── History skeleton ─────────────────────────────────────────────────────── */
function HistorySkeleton() {
    return (
        <div className="hw-pe--history-grid">
            {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="hw-pe--history-card">
                    <div className="hw-pe--history-thumb-wrap">
                        <div className="hw-pe--skel-thumb" />
                    </div>
                    <div className="hw-pe--history-meta">
                        <div className="hw-pe--skel-line" style={{ width: '70%', margin: '0 0 0.8rem 0' }} />
                        <div className="hw-pe--skel-line-sm" style={{ width: '50%', margin: '0 0 0.6rem 0' }} />
                        <div className="hw-pe--skel-line-sm" style={{ width: '40%', margin: '0 0 1.2rem 0' }} />
                        <div className="d-flex hw-gapx8" style={{ marginTop: 'auto' }}>
                            <div className="hw-pe--skel-line-btn" style={{ flex: 1 }} />
                            <div className="hw-pe--skel-line-btn" style={{ width: '3.2rem' }} />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );
}

/* ─── Pagination helper ────────────────────────────────────────────────────── */
function buildPageNums(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const pages = [];
    if (current <= 4) {
        pages.push(1, 2, 3, 4, 5, '…', total);
    } else if (current >= total - 3) {
        pages.push(1, '…', total - 4, total - 3, total - 2, total - 1, total);
    } else {
        pages.push(1, '…', current - 1, current, current + 1, '…', total);
    }
    return pages;
}

/* ─── Format date ──────────────────────────────────────────────────────────── */
function fmtDate(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ─── Backend-proxied download (avoids S3 CORS restrictions) ──────────────── */
async function downloadPhoto(jobId, filename) {
    try {
        // ApiCall with responseType:'blob' tells axios to return a Blob
        // The backend fetches from S3 and streams it with Content-Disposition: attachment
        const res = await ApiCall(
            'GET',
            `/photo/download?jobId=${encodeURIComponent(jobId)}`,
            { responseType: 'blob' },
            {}
        );
        const blob = res?.data;
        if (!blob) throw new Error('Empty response');
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename || 'passport_photo.jpg';
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(blobUrl);
    } catch (err) {
        console.error('Download failed:', err);
    }
}

/* ─── Download button with loading state ──────────────────────────────────── */
function DownloadBtn({ jobId, filename, className, children }) {
    const [busy, setBusy] = useState(false);
    const handleClick = async (e) => {
        e.stopPropagation();
        if (busy) return;
        setBusy(true);
        await downloadPhoto(jobId, filename);
        setBusy(false);
    };
    return (
        <button
            className={className}
            onClick={handleClick}
            disabled={busy}
            style={{ opacity: busy ? 0.7 : 1, cursor: busy ? 'wait' : 'pointer' }}
        >
            {busy ? <span style={{ fontSize: '1.1rem' }}>Downloading…</span> : children}
        </button>
    );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════════════════════════════════════════════ */
function PhotoEditorPage() {
    /* ── wizard state ─────────────────────────────────────────────────────── */
    const [step, setStep] = useState(1);
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);
    const [countriesErr, setCountriesErr] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [originalDataUrl, setOriginalDataUrl] = useState(null);
    const [inputMode, setInputMode] = useState('upload'); // 'upload' | 'camera'
    const [isProcessing, setIsProcessing] = useState(false);
    const [progressIdx, setProgressIdx] = useState(0);
    const [progressPct, setProgressPct] = useState(0);
    const [processErr, setProcessErr] = useState('');
    const [isAiMode, setIsAiMode] = useState(null);
    const [result, setResult] = useState(null);
    const timerRef = useRef(null);
    const [paymentError, setPaymentError] = useState('');
    const [showPaymentPopup, setShowPaymentPopup] = useState(false);

    /* ── history state ────────────────────────────────────────────────────── */
    const [history, setHistory] = useState([]);
    const [historyPage, setHistoryPage] = useState(1);
    const [historyTotalPages, setHistoryTotalPages] = useState(1);
    const [historyTotal, setHistoryTotal] = useState(0);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null); // jobId to delete, or null
    const userToken = getUserToken();

    /* ── boot ─────────────────────────────────────────────────────────────── */
    useEffect(() => {
        const initData = async () => {
            const [countriesResp, healthResp] = await Promise.all([
                ApiCall('GET', '/photo/countries', null, {}),
                ApiCall('GET', '/photo/health', null, {}),
            ]);

            if (countriesResp?.data?.status === 'success') {
                setCountries(countriesResp.data.data.countries || []);
            } else {
                setCountriesErr('Could not load countries. Please make sure the photo service is running.');
            }
            setIsAiMode(
                healthResp?.data?.status === 'success'
                    ? !!healthResp.data.data.gemini
                    : false
            );
        };
        initData();
        fetchHistory(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ── fetch history ────────────────────────────────────────────────────── */
    const fetchHistory = useCallback(async (page) => {
        setHistoryLoading(true);
        try {
            const res = await ApiCall('GET', `/photo/history?page=${page}&limit=5`, null, {});
            if (res?.data?.status === 'success') {
                const { records, pagination } = res.data.data;
                setHistory(records || []);
                setHistoryPage(pagination.page);
                setHistoryTotalPages(pagination.totalPages);
                setHistoryTotal(pagination.total);
            }
        } catch (_) { /* silent */ }
        setHistoryLoading(false);
    }, []);

    /* ── delete history record ────────────────────────────────────────────── */
    const confirmDeleteHistory = useCallback(async () => {
        if (!deleteConfirm) return;
        const jobId = deleteConfirm;
        setDeleteConfirm(null);
        try {
            const res = await ApiCall('DELETE', `/photo/history/${jobId}`, null, {});
            if (res?.data?.status === 'success') {
                const newList = history.filter(r => r.jobId !== jobId);
                const newTotal = Math.max(0, historyTotal - 1);
                setHistory(newList);
                setHistoryTotal(newTotal);
                if (newList.length === 0 && historyPage > 1) {
                    fetchHistory(historyPage - 1);
                }
            }
        } catch (_) { /* silent */ }
    }, [deleteConfirm, history, historyTotal, historyPage, fetchHistory]);

    /* ── progress ─────────────────────────────────────────────────────────── */
    const progressSteps = ['Uploading photo…', 'Analyzing photo…', 'Generating passport photo…', 'Applying country specs…', 'Finalizing…'];

    const startProgress = useCallback(() => {
        setProgressIdx(0); setProgressPct(5);
        let i = 0;
        timerRef.current = setInterval(() => {
            i += 1;
            if (i < progressSteps.length) {
                setProgressIdx(i);
                setProgressPct(Math.round(((i + 1) / progressSteps.length) * 85));
            }
        }, 5000);
    }, [progressSteps.length]);

    const stopProgress = useCallback((ok) => {
        clearInterval(timerRef.current);
        if (ok) { setProgressIdx(progressSteps.length); setProgressPct(100); }
        setTimeout(() => { setIsProcessing(false); setProgressPct(0); setProgressIdx(0); }, 700);
    }, [progressSteps.length]);

    useEffect(() => () => clearInterval(timerRef.current), []);

    /* ── file ─────────────────────────────────────────────────────────────── */
    const handleFile = (file) => {
        setSelectedFile(file);
        setProcessErr('');
        const reader = new FileReader();
        reader.onload = e => setOriginalDataUrl(e.target.result);
        reader.readAsDataURL(file);
    };
    const removeFile = () => { setSelectedFile(null); setOriginalDataUrl(null); setProcessErr(''); };

    const generateNonceForTranscationFn = async (transcationType = 'wallet', type = 'debit') => {
        let nonceToken = '';
        const nonceData = await GetApiCall('GET', `wp-json/dahr/v1/generate-nonce?userToken=${userToken}&transaction_type=${type}&type=${transcationType}`, {}, 'wordpress');
        if (nonceData?.status === 200) {
            nonceToken = nonceData?.data?.nonce;
        }
        return nonceToken;
    }

    /* ── process ──────────────────────────────────────────────────────────── */
    const processPhoto = async () => {
        setPaymentError('');
        if (!selectedFile || !selectedCountry) return;
        setProcessErr(''); setIsProcessing(true); startProgress();
        const form = new FormData();
        form.append('photo', selectedFile);
        form.append('country', selectedCountry.id);
        try {
            const walletBalanceData = await GetApiCall('GET', `wp-json/dahr/v1/user-wallet-balance?userToken=${userToken}&activeUserCurrency=AED`, {}, 'wordpress');
            if (walletBalanceData?.status === 200) {
                let walletBalance = walletBalanceData?.data?.wallet_balance;
                if (parseFloat(walletBalance) > 1) {
                    let nonceToken = await generateNonceForTranscationFn('wallet', 'debit');
                    const deductBalanceFromWallet = await ApiCall('POST', `wp-json/dahr/v1/wallet-transaction`, {
                        userToken: userToken,
                        activeUserCurrency: 'AED',
                        display_currency: getActiveCode(),
                        conversion_rate: getActiveRate(),
                        transaction_type: "debit",
                        payment_mode: "wallet",
                        nonce: nonceToken,
                        amount: 1,
                        transaction_comment: `Debited Towards : Photo Generation`
                    }, {}, 'wordpress');
                    if ((deductBalanceFromWallet?.status === 200 && deductBalanceFromWallet?.data?.success && deductBalanceFromWallet?.data?.transaction_id) || (activeCouponCodeData?.discount_value && totalPrice === 0)) {
                        const res = await ApiCall('POST', '/photo/generate', form, {});
                        if (!res || res.data?.status !== 'success') {
                            throw new Error(res?.data?.message || `Server error (${res?.status || 'unknown'})`);
                        }
                        const data = res.data.data;
                        stopProgress(true);
                        setResult(data);
                        setStep(3);
                        // Refresh history so the new record appears immediately
                        fetchHistory(1);
                    } else {
                        stopProgress(false);
                        setIsProcessing(false);
                        setPaymentError('Wallet Balance not deduct.');
                    }
                } else {
                    stopProgress(false);
                    setIsProcessing(false);
                    setPaymentError(`Insufficient Balance. Your Total Balance is: ${walletBalance} <br/>
                    <a target="_blank" href="${process.env.wordpressApiEndPoint}dashboard/?tab=new-balance-request" class="hw-link--line">Add your Balance</a>`);
                }
            } else {
                stopProgress(false);
                setIsProcessing(false);
                setPaymentError('Could not verify wallet balance. Please try again later.');
            }
        } catch (err) {
            stopProgress(false);
            setIsProcessing(false);
            setProcessErr(err.message || 'Something went wrong. Please try again.');
            let nonceToken = await generateNonceForTranscationFn('wallet', 'credit');
            await ApiCall('POST', `wp-json/dahr/v1/wallet-transaction`, {
                userToken: userToken,
                activeUserCurrency: 'AED',
                display_currency: getActiveCode(),
                conversion_rate: getActiveRate(),
                transaction_type: "credit",
                payment_mode: "wallet",
                amount: 1,
                nonce: nonceToken,
                transaction_comment: `Credited Towards: Photo Generation Error`
            }, {}, 'wordpress')
        }
    };

    /* ── restart ──────────────────────────────────────────────────────────── */
    const restart = () => {
        setStep(1); setSelectedCountry(null); setSelectedFile(null);
        setOriginalDataUrl(null); setResult(null); setProcessErr('');
    };

    /* ── country options for react-select ────────────────────────────────── */
    const countryOptions = countries.map(c => ({ ...c, value: c.id, label: c.name }));

    /* ── pagination page numbers ──────────────────────────────────────────── */
    const pageNums = buildPageNums(historyPage, historyTotalPages);

    /* ─────────────────────────────────────────────────────────────────────── */
    return (
        <div className="hw-page--width hw-pe--page">

            {/* ── Page title bar ─────────────────────────────────────────────── */}
            <div className="hw-pe--page-header">
                <div className="hw-pe--page-header-icon">
                    <IcoCamera size={22} />
                </div>
                <div style={{ flex: 1 }}>
                    <h2 className="h2 hw-blue mb-0">Passport Photo Maker</h2>
                    <p className="hw-text14 hw-grayk500 mb-0">
                        Professional photo processing for any country's document requirements
                    </p>
                </div>
            </div>

            {/* ── Stepper ────────────────────────────────────────────────────── */}
            <Stepper step={step} />

            {/* ══════════ STEP 1 — Choose country ═══════════════════════════════ */}
            {step === 1 && (
                <div className="hw-pe--card">
                    <p className="hw-text20 hw-fontBold hw-black900 mb-02">Choose your document type</p>
                    <p className="hw-text14 hw-grayk500 mb-3">
                        Select the country and document you need the photo for
                    </p>

                    {countriesErr && (
                        <div className="hw-pe--alert hw-pe--alert-error d-flex align-items-start hw-gapy8 mb-2">
                            <IcoWarn /> <span className="hw-text14">{countriesErr}</span>
                        </div>
                    )}

                    <div className="hw-pe--step1-layout">
                        <div>
                            <Select
                                options={countryOptions}
                                value={selectedCountry ? { ...selectedCountry, value: selectedCountry.id, label: selectedCountry.name } : null}
                                onChange={opt => setSelectedCountry(opt)}
                                placeholder="Search country or document…"
                                formatOptionLabel={formatOptionLabel}
                                styles={rsStyles}
                                isSearchable
                                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                menuPosition="fixed"
                                noOptionsMessage={() => 'No matching country found'}
                            />
                            <button
                                className="hw-btn hw-btn--primary d-flex align-items-center hw-gapy8 mt-3"
                                disabled={!selectedCountry}
                                onClick={() => setStep(2)}
                                style={{ opacity: selectedCountry ? 1 : 0.5, cursor: selectedCountry ? 'pointer' : 'not-allowed' }}
                            >
                                Continue <IcoArrow />
                            </button>
                            <div className="hw-pe--demo-wrap mt-4">
                                <div className="hw-pe--demo-title hw-text14 hw-fontBold hw-brand-blue mb-2">
                                    Instant Passport Photo Transformation
                                </div>
                                <div className="hw-pe--demo-grid">
                                    <div className="hw-pe--demo-card">
                                        <div className="hw-pe--demo-img-wrap">
                                            <img src={imgBefore} alt="Before conversion" className="hw-pe--demo-img" />
                                            <span className="hw-pe--demo-badge badge-before">Before</span>
                                        </div>
                                        <div className="hw-pe--demo-list mt-1">
                                            <div className="hw-pe--demo-item item-bad">
                                                <IcoCross size={8} /> <span>Uneven background</span>
                                            </div>
                                            <div className="hw-pe--demo-item item-bad">
                                                <IcoCross size={8} /> <span>Casual angle & shadows</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="hw-pe--demo-arrow-indicator">
                                        <IcoArrow size={16} />
                                    </div>
                                    <div className="hw-pe--demo-card">
                                        <div className="hw-pe--demo-img-wrap">
                                            <img src={imgAfter} alt="After conversion" className="hw-pe--demo-img" />
                                            <span className="hw-pe--demo-badge badge-after">After</span>
                                        </div>
                                        <div className="hw-pe--demo-list mt-1">
                                            <div className="hw-pe--demo-item item-good">
                                                <IcoCheck size={8} /> <span>Solid white background</span>
                                            </div>
                                            <div className="hw-pe--demo-item item-good">
                                                <IcoCheck size={8} /> <span>Compliant & cropped</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="hw-pe--demo-divider" />
                                <div className="hw-pe--demo-grid">
                                    <div className="hw-pe--demo-card">
                                        <div className="hw-pe--demo-img-wrap">
                                            <img src={imgBefore2} alt="Before critical conversion" className="hw-pe--demo-img" />
                                            <span className="hw-pe--demo-badge badge-before">Before</span>
                                        </div>
                                        <div className="hw-pe--demo-list mt-1">
                                            <div className="hw-pe--demo-item item-bad">
                                                <IcoCross size={8} /> <span>Cluttered background</span>
                                            </div>
                                            <div className="hw-pe--demo-item item-bad">
                                                <IcoCross size={8} /> <span>Uneven face shadows</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="hw-pe--demo-arrow-indicator">
                                        <IcoArrow size={16} />
                                    </div>
                                    <div className="hw-pe--demo-card">
                                        <div className="hw-pe--demo-img-wrap">
                                            <img src={imgAfter2} alt="After critical conversion" className="hw-pe--demo-img" />
                                            <span className="hw-pe--demo-badge badge-after">After</span>
                                        </div>
                                        <div className="hw-pe--demo-list mt-1">
                                            <div className="hw-pe--demo-item item-good">
                                                <IcoCheck size={8} /> <span>Solid off-white background</span>
                                            </div>
                                            <div className="hw-pe--demo-item item-good">
                                                <IcoCheck size={8} /> <span>Perfectly balanced lighting</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <SpecPreview country={selectedCountry} />
                    </div>
                </div>
            )}

            {/* ══════════ STEP 2 — Upload ════════════════════════════════════════ */}
            {step === 2 && (
                <div className="hw-pe--card">
                    <button className="hw-pe--back-btn" onClick={() => { removeFile(); setStep(1); }}>
                        <IcoBack /> Back
                    </button>

                    <p className="hw-text20 hw-fontBold hw-black900 mb-02">
                        Upload your {selectedCountry?.name || ''} photo
                    </p>
                    <p className="hw-text14 hw-grayk500 mb-3">
                        Dahr Tours will process it to meet {selectedCountry?.name || ''} requirements
                    </p>

                    <div className="hw-pe--upload-layout">
                        {/* ── Requirements sidebar ── */}
                        <div className="hw-pe--req-panel">
                            <div className="d-flex align-items-center hw-gapy8 mb-2">
                                <span style={{ fontSize: '1.8rem', lineHeight: 1 }}>{selectedCountry?.flag}</span>
                                <span className="hw-text14 hw-fontBold hw-black900">{selectedCountry?.name}</span>
                            </div>
                            <ul className="hw-pe--req-list">
                                {(selectedCountry?.requirements || []).map((r, i) => (
                                    <li key={i} className="d-flex align-items-start hw-gapy8 hw-text12 hw-black500">
                                        <span className="hw-pe--req-icon"><IcoCheck size={8} /></span>
                                        {r}
                                    </li>
                                ))}
                            </ul>
                            <div className="hw-text12 hw-grayk500 mt-2"
                                style={{ paddingTop: '0.8rem', borderTop: '1px solid var(--hw-gray-200)', textAlign: 'center' }}>
                                <strong>{selectedCountry?.widthMm}×{selectedCountry?.heightMm} mm</strong>
                                &nbsp;·&nbsp; {selectedCountry?.widthPx}×{selectedCountry?.heightPx} px
                            </div>
                        </div>

                        {/* ── Upload / Capture area ── */}
                        <div>
                            {!selectedFile && (
                                <>
                                    <div className="d-flex align-items-center justify-content-center mb-2" style={{ gap: 8 }}>
                                        <button type="button"
                                            className={`hw-btn ${inputMode === 'upload' ? 'hw-btn--primary' : 'hw-btn--outline'}`}
                                            onClick={() => setInputMode('upload')}>
                                            Upload
                                        </button>
                                        <button type="button"
                                            className={`hw-btn ${inputMode === 'camera' ? 'hw-btn--primary' : 'hw-btn--outline'}`}
                                            onClick={() => setInputMode('camera')}>
                                            Capture
                                        </button>
                                    </div>
                                    {inputMode === 'upload'
                                        ? <DropZone onFile={handleFile} />
                                        : <CameraCapture onCapture={handleFile} />}
                                </>
                            )}

                            {selectedFile && originalDataUrl && (
                                <div className="hw-pe--preview-wrap">
                                    <img className="hw-pe--preview-img" src={originalDataUrl} alt="Preview" />
                                    <button className="hw-pe--remove-btn" onClick={removeFile} title="Remove">✕</button>
                                </div>
                            )}

                            {isProcessing && (
                                <ProgressPanel steps={progressSteps} idx={progressIdx} pct={progressPct} />
                            )}

                            {processErr && (
                                <div className="hw-pe--alert hw-pe--alert-error d-flex align-items-start hw-gapy8 mt-2">
                                    <IcoWarn /> <span className="hw-text14">{processErr}</span>
                                </div>
                            )}

                            <button
                                className="hw-btn hw-btn--primary hw-pe--process-btn mt-2"
                                disabled={!selectedFile || isProcessing}
                                onClick={() => setShowPaymentPopup(true)}
                                style={{ opacity: (!selectedFile || isProcessing) ? 0.5 : 1 }}
                            >
                                {isProcessing ? 'Processing…' : <>Pay {formatePriceFn('AED', formatPrice(1))} & Generate Passport Photo</>}
                            </button>

                            {
                                paymentError && (
                                    <div className="hw-pe--alert hw-pe--alert-error d-flex align-items-start hw-gapy8 mt-2">
                                        <IcoWarn /> <span className="hw-text14" dangerouslySetInnerHTML={{ __html: paymentError }} />
                                    </div>
                                )
                            }

                            {/* Photo tips */}
                            <div className="hw-pe--tips-row mt-3">
                                {[
                                    'Good natural lighting, no harsh shadows',
                                    'Any background - will replace it automatically',
                                    'Look straight at camera, neutral expression',
                                    'Full face visible - no hats or sunglasses',
                                ].map((tip, i) => (
                                    <div key={i} className="hw-pe--tip-card d-flex align-items-start hw-gapy8 hw-text12">
                                        <IcoInfo size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                                        {tip}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ══════════ STEP 3 — Result ════════════════════════════════════════ */}
            {step === 3 && result && (
                <div className="hw-pe--result-container">
                    <div className="hw-pe--result-header-bar mb-3">
                        <button className="hw-pe--back-btn-new" onClick={() => { removeFile(); setStep(2); }}>
                            <IcoBack /> Back to Upload
                        </button>
                        <div className="hw-pe--result-header-status hw-text16 hw-fontBold hw-brand-blue">
                            <IcoCheck size={14} /> Photo Processed Successfully
                        </div>
                    </div>

                    <AnalysisPanel analysis={result.analysis} />

                    {(result.warnings || []).length > 0 && (
                        <div className="mb-3">
                            {result.warnings.map((w, i) => (
                                <div key={i} className="hw-pe--alert hw-pe--alert-warn d-flex align-items-start hw-gapy8 mb-1">
                                    <IcoWarn /> <span className="hw-text14">{w}</span>
                                </div>
                            ))}
                        </div>
                    )}

                    <div className="hw-pe--result-layout mb-2">
                        <div className="hw-pe--result-card">
                            <div className="hw-pe--result-card-header">
                                <span className="hw-pe--result-card-title hw-text12 hw-fontBold">Original Photo</span>
                                <span className="hw-pe--tag-new tag-raw hw-text12 hw-fontBold">Raw Upload</span>
                            </div>
                            <div className="hw-pe--result-image-container">
                                <img src={originalDataUrl} alt="Original" className="hw-pe--result-image" />
                            </div>
                        </div>

                        <div className="hw-pe--result-card">
                            <div className="hw-pe--result-card-header">
                                <span className="hw-pe--result-card-title hw-text12 hw-fontBold">Processed Photo</span>
                                <span className="hw-pe--tag-new tag-compliant hw-text12 hw-fontBold">✓ Compliant</span>
                            </div>
                            <div className="hw-pe--result-image-container">
                                <img src={result.photoUrl} alt="Processed" className="hw-pe--result-image" />
                            </div>
                        </div>
                    </div>

                    <div className="hw-pe--control-panel mt-3">
                        <div className="hw-pe--metadata-ribbon mb-3">
                            {result.totalMs && (
                                <div className="hw-pe--meta-pill">
                                    <IcoClock size={12} />
                                    <span className="hw-text12">{`Processed in ${(result.totalMs / 1000).toFixed(1)}s`}</span>
                                </div>
                            )}
                            <div className="hw-pe--meta-pill">
                                <span className="hw-pe--meta-flag hw-text12">{result.country?.flag}</span>
                                <span className="hw-text12">{result.country?.name} Spec</span>
                            </div>
                            {result.country?.widthPx && (
                                <div className="hw-pe--meta-pill">
                                    <IcoHash size={12} />
                                    <span className="hw-text12">{`${result.country?.widthPx} × ${result.country?.heightPx} px (${result.country?.widthMm}×${result.country?.heightMm} mm)`}</span>
                                </div>
                            )}
                        </div>

                        <div className="hw-pe--action-row">
                            <DownloadBtn
                                jobId={result.jobId}
                                filename={`${result.country?.id || 'passport'}_photo.jpg`}
                                className="hw-pe--download-primary"
                            >
                                <IcoDL size={16} /> Download Passport Photo
                            </DownloadBtn>
                            <button
                                className="hw-pe--restart-secondary"
                                onClick={restart}
                            >
                                <IcoRestart size={14} /> Process Another Photo
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══════════ PHOTO HISTORY ═══════════════════════════════════════════ */}
            <div className="hw-pe--history-section">
                {/* Section header */}
                <div className="hw-pe--history-hd">
                    <div className="hw-pe--history-hd-icon">
                        <IcoClock size={18} />
                    </div>
                    <div className="hw-pe--history-hd-text">
                        <p className="hw-text16 hw-fontBold hw-black900 mb-0">Photo History</p>
                        <p className="hw-text12 hw-grayk500 mb-0">
                            {historyTotal > 0
                                ? `${historyTotal} photo${historyTotal !== 1 ? 's' : ''} generated`
                                : 'Your generated photos will appear here'}
                        </p>
                    </div>
                </div>

                {/* Loading skeletons */}
                {historyLoading && <HistorySkeleton />}

                {/* Empty state */}
                {!historyLoading && history.length === 0 && (
                    <div className="hw-pe--history-empty">
                        <div className="hw-pe--history-empty-ico">
                            <IcoClock size={40} />
                        </div>
                        <p className="hw-text14 hw-fontBold hw-black900 mb-02">No photos yet</p>
                        <p className="hw-text12 hw-grayk500 mb-0">
                            Generate your first passport photo using the form above
                        </p>
                    </div>
                )}

                {/* History grid */}
                {!historyLoading && history.length > 0 && (
                    <>
                        <div className="hw-pe--history-grid">
                            {history.map(record => (
                                <div key={record._id || record.jobId} className="hw-pe--history-card">
                                    <div className="hw-pe--history-thumb-wrap">
                                        <img
                                            src={record.photoUrl}
                                            alt={record.countryName || 'Passport photo'}
                                            className="hw-pe--history-thumb"
                                        />
                                        <span className="hw-pe--history-badge">
                                            <IcoCheck size={10} /> Compliant
                                        </span>
                                    </div>
                                    <div className="hw-pe--history-meta">
                                        <div className="d-flex align-items-center justify-between mb-1">
                                            <span className="hw-pe--history-flag">{record.countryFlag}</span>
                                            <span className="hw-text14 hw-fontBold hw-black900 overflow-ellipsis" style={{ lineHeight: 1.2 }}>
                                                {record.countryName || '—'}
                                            </span>
                                        </div>
                                        <div className="hw-pe--history-details">
                                            <div className="hw-pe--hd-item">
                                                <IcoCalendar size={12} />
                                                <span>{fmtDate(record.createdAt)}</span>
                                            </div>
                                            <div className="hw-pe--hd-item">
                                                <IcoHash size={12} />
                                                <span>ID: {record.jobId ? record.jobId.slice(-8).toUpperCase() : '—'}</span>
                                            </div>
                                        </div>
                                        <div className="hw-pe--history-actions">
                                            <DownloadBtn
                                                jobId={record.jobId}
                                                filename={`${record.countryId || 'passport'}_photo.jpg`}
                                                className="hw-pe--action-dl"
                                            >
                                                <IcoDL size={12} /> Download
                                            </DownloadBtn>
                                            {/* <button
                                                className="hw-pe--action-del"
                                                onClick={e => { e.stopPropagation(); setDeleteConfirm(record.jobId); }}
                                                title="Remove photo"
                                            >
                                                <IcoTrash size={12} />
                                            </button> */}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {historyTotalPages > 1 && (
                            <div className="hw-pe--pagination-wrap mt-4">
                                <div className="hw-pe--pagination-info hw-text12 hw-grayk500">
                                    Showing page <strong>{historyPage}</strong> of <strong>{historyTotalPages}</strong> &nbsp;·&nbsp; <strong>{historyTotal}</strong> photo{historyTotal !== 1 ? 's' : ''} generated
                                </div>
                                <div className="hw-pe--pagination">
                                    <button
                                        className="hw-pe--pg-btn"
                                        disabled={historyPage <= 1}
                                        onClick={() => fetchHistory(historyPage - 1)}
                                        aria-label="Previous page"
                                    >
                                        <IcoChevL size={14} /> <span className="hw-pe--pg-txt">Previous</span>
                                    </button>

                                    {pageNums.map((p, i) =>
                                        p === '…' ? (
                                            <span key={`ellipsis-${i}`}
                                                style={{ padding: '0 0.5rem', color: 'var(--hw-gray-500)', fontSize: '1.3rem' }}>
                                                …
                                            </span>
                                        ) : (
                                            <button
                                                key={p}
                                                className={`hw-pe--pg-btn${p === historyPage ? ' active' : ''}`}
                                                onClick={() => p !== historyPage && fetchHistory(p)}
                                            >
                                                {p}
                                            </button>
                                        )
                                    )}

                                    <button
                                        className="hw-pe--pg-btn"
                                        disabled={historyPage >= historyTotalPages}
                                        onClick={() => fetchHistory(historyPage + 1)}
                                        aria-label="Next page"
                                    >
                                        <span className="hw-pe--pg-txt">Next</span> <IcoChevR size={14} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ══════════ DELETE CONFIRM POPUP ════════════════════════════════════ */}
            <CommonPopup
                show={!!deleteConfirm}
                setShow={(v) => { if (!v) setDeleteConfirm(null); }}
                title={
                    <span className="d-flex align-items-center hw-gapy8">
                        <IcoTrash size={16} /> Remove Photo
                    </span>
                }
                modalClass="hw-modal--medium"
            >
                <div>
                    <p className="hw-text14 hw-black500 mb-3">
                        Are you sure you want to remove this photo from your history?
                        This action cannot be undone.
                    </p>
                    <div className="d-flex align-items-center hw-gapy8" style={{ justifyContent: 'flex-end' }}>
                        <button
                            className="hw-btn"
                            style={{ border: '1.5px solid var(--hw-gray-200)', background: '#fff', color: 'var(--hw-gray-650)' }}
                            onClick={() => setDeleteConfirm(null)}
                        >
                            Cancel
                        </button>
                        <button
                            className="hw-btn hw-btn--primary"
                            style={{ background: 'var(--hw-red)', borderColor: 'var(--hw-red)' }}
                            onClick={confirmDeleteHistory}
                        >
                            <IcoTrash size={13} /> Remove
                        </button>
                    </div>
                </div>
            </CommonPopup>
            <CommonPopup
                show={!!showPaymentPopup}
                setShow={(v) => { if (!v) setShowPaymentPopup(false); }}
                title={
                    <span className="d-flex align-items-center hw-gapy8">
                        Confirm Payment
                    </span>
                }
                modalClass="hw-modal--medium"
            >
                <div>
                    <p className="hw-text14 hw-black500 mb-3">
                        Are you sure you want to proceed with the payment to generate the passport photo?
                        <b> Charges : {formatePriceFn('AED', formatPrice(1))} </b>
                    </p>
                    <div className="d-flex align-items-center hw-gapy8" style={{ justifyContent: 'flex-end' }}>
                        <button
                            className="hw-btn"
                            style={{ border: '1.5px solid var(--hw-gray-200)', background: '#fff', color: 'var(--hw-gray-650)' }}
                            onClick={() => setShowPaymentPopup(false)}
                        >
                            Cancel
                        </button>
                        <button
                            className="hw-btn hw-btn--primary"
                            style={{ background: 'var(--hw-brand-orange)', borderColor: 'var(--hw-red)' }}
                            onClick={() => { setShowPaymentPopup(false); processPhoto(); }}
                        >
                            Proceed
                        </button>
                    </div>
                </div>
            </CommonPopup>

        </div>
    );
}

export default PhotoEditorPage;
